// src/pages/Authentication/LoginTest.js
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { 
  Row, 
  Col, 
  CardBody, 
  Card, 
  Alert, 
  Container, 
  Form, 
  Input, 
  FormFeedback, 
  Label 
} from "reactstrap";
import withRouter from "components/Common/withRouter";
import { useTranslation } from 'react-i18next';

// Custom Axios
import axiosInstance from 'utils/axiosInstance';
import LanguageSwitcher from 'components/Common/LanguageSwitcher';

// Redux
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// actions
import { 
  loginUser, 
  logoutUser,
  clearRegisterUser, 
  clearForgetPassword, 
  clearChangePassword, 
  googleLoginRequest,
  clearLoginStates,
  clearGoogleLoginStates,
} from "../../store/actions";

// Import the selectors
import { 
  getLoginError, 
  getLoginLoading, 
  getIsLoggedIn, 
  getGoogleError, 
  getGoogleLoading, 
  getGoogleLoggedIn 
} from 'store/selectors';

import OrDivider from "./components/OrDivider";

// Custom Images
import profile from "assets/images/profile-img.png";
//import logo from "assets/images/logo.svg";
import perlTea from "assets/images/perlTea12.jpeg";
import Christmas_Landing from "assets/images/TheChart_landing_Christmas2024.gif";
import perlTeaLogo from "assets/images/perlTea2.jpeg";

import logo from "assets/images/logo/ttmy_logo.png";

const errorMessagesMap = {
  "Login failed": "帳號或密碼錯誤",
  "Another error": "Another custom message",
};


// Main Component
const Login = props => {


  /*---- Title and Initial Setup ----*/

  document.title = "帳號登入 | TheChart.AI";
  const { t } = useTranslation('Login');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [viewPassword, setViewPassword] = useState(false);
  const [saveAccount, setSaveAccount] = useState(false);
  const [initialEmail, setInitialEmail] = useState('');
  const [isUserGoBack, setIsUserGoBack] = useState(false);

  // Clear authUser from localStorage upon loading Login Page
  // Clear status in register, forget-password, and change-password
  useEffect(() => {

    // Do not remove this logic below!! This is necessary for user clicking GO BACK from dashboard
    const thisAuthUser = localStorage.getItem('authUser');
    if(thisAuthUser){
      localStorage.setItem('isUserGoBack', true);
    } else {
      localStorage.removeItem('isUserGoBack');
    }
    localStorage.removeItem('authUser');
    console.log('[Login.js] authUser removed!')
    // Do not remove this logic above!! This is necessary for user clicking GO BACK from dashboard

    //dispatch(logoutUser(navigate));

    dispatch(clearRegisterUser());
    dispatch(clearForgetPassword());
    dispatch(clearChangePassword());
    if(isLoggedIn){setIsUserGoBack(true)};
  }, []);

  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setViewPassword(!viewPassword);
  };

  useEffect(() => {
    // Check if there is an account saved in local storage
    const savedAccount = localStorage.getItem('account');
    console.log('savedAccount:', savedAccount);
    if (savedAccount) {
      setInitialEmail(savedAccount);
      setSaveAccount(true);
    }
  }, []);

  const handleSaveAccountChange = (e) => {
    setSaveAccount(!saveAccount);
  };

  /*---- Title and Initial Setup Tail ----*/



  /*---- Store States and Changes ----*/

  // Use memoized selectors
  const error = useSelector(getLoginError);
  const loading = useSelector(getLoginLoading);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const googleError = useSelector(getGoogleError);
  const googleLoading = useSelector(getGoogleLoading);
  const googleLoggedIn = useSelector(getGoogleLoggedIn);


  // Redirect after login success
  useEffect(() => { 
    const thisUserGoBack = localStorage.getItem('isUserGoBack');
    if (isLoggedIn && !thisUserGoBack) { navigate('/myInfo/storeMenu'); }
  }, [isLoggedIn]);


  useEffect(() => { 
    const thisUserGoBack = localStorage.getItem('isUserGoBack');
    if (googleLoggedIn && !thisUserGoBack) { navigate('/myInfo/storeMenu'); }
  }, [googleLoggedIn]);



  const [errorMessage, setErrorMessage] = useState("");
  const [googleErrorMessage, setGoogleErrorMessage] = useState("");
  
  useEffect(() => {
    console.log("error changed:", error);
    setErrorMessage(errorMessagesMap[error] || error);
  }, [error]);

  useEffect(() => {
    console.log("google error changed:", googleError);
    setGoogleErrorMessage(errorMessagesMap[googleError] || googleError);
  }, [googleError]);

  /*---- Store States and Changes Tail ----*/





  /*---- Login Form ----*/

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      email: initialEmail || '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Email 不能留空"),
      password: Yup.string().required("密碼不能留空"),
    }),

    onSubmit: async (values) => {
      if (saveAccount) { localStorage.setItem('account', values.email);}
      if (!saveAccount) { localStorage.removeItem('account');}

      // Dispatch the login action
      dispatch(loginUser(values, props.router.navigate));
    }

  });

  // Pressing 'enter' when in password field or clicking Login button
  function handleSubmit(event) {
    event.preventDefault(); // Prevent the default form submission behavior
    localStorage.removeItem('isUserGoBack');

    console.log('clicked!');

    // Manually set all fields as touched
    validation.setTouched({
      email: true,
      password: true,
    });

    validation.handleSubmit();
    
  }

  /*---- Login Form Tail ----*/
  



  /*---- Loading Google Script ----*/

  const [isGoogleApiLoaded, setIsGoogleApiLoaded] = useState(false);

  useEffect(() => {
    const loadGoogleScript = () => {
      const script = document.createElement('script');
      script.src = "https://accounts.google.com/gsi/client";
      script.onload = () => setIsGoogleApiLoaded(true);
      document.body.appendChild(script);
    };

    if (!window.google) {
      loadGoogleScript();
    } else {
      setIsGoogleApiLoaded(true);
    }
  }, []);

  /*---- Loading Google Script Tail ----*/



  /*---- Google Login Button ----*/

  const divRef = useRef(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (isGoogleApiLoaded && divRef.current) {
      const handleCredentialResponse = async (res, error) => {
        //-localStorage.removeItem('isUserGoBack');
        dispatch(googleLoginRequest(res));
      }
      google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (res, error)=>{ handleCredentialResponse(res, error) },
      });
      google.accounts.id.renderButton( document.getElementById("buttonDiv"), { 
        theme: "outline", 
        size: "large", // large/medium
        type: 'standard',
        text: 'signin-with',
        shape: 'rectangular', // rectangular/pill
        logo_alignment: 'left',
        width: '220',
      });
      //google.accounts.id.prompt(); // also display the One Tap dialog
    }
  }, [isGoogleApiLoaded, divRef, dispatch]);

  /*---- Google Login Button Tail ----*/


  /*---- Send Verification email ----*/

  const resendVerificationEmail = async (email) => {
    try {
      const response = await axiosInstance.post('/knovia/resend-verification', { email });
      alert(`已重新寄出驗證信至${email}，請前往驗證帳號`);
      dispatch(clearLoginStates());
    } catch (error) {
      console.error('Error resending verification email:', error);
      alert('重新發送驗證信時發生錯誤，請稍後再試');
      dispatch(clearLoginStates());
    }
  };

  /*---- Send Verification email Tail ----*/



  /*---- Return Component ----*/

  return (
    <React.Fragment>
      <div className="account-pages" style={{ marginTop: "0rem", padding: '0' }}>
        <Container style={{ maxWidth:'100%', overflow: 'hidden' }}>
          <Row
            style={{
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'white'
            }}
          >

            <Col sm={12} md={12}
              style={{
                padding: '0',
                maxWidth: '700px'
              }}
            >
              <Card className="" 
                style={{ 
                  height: '100vh', 
                  margin:'0', 
                  overflowY: 'scroll',
                  overflowX: 'hidden' 
                }}
              > 
                {/*
                <div style={{ backgroundColor: '#ddf0ff' }}>
                  <Row>
                    <Col xs={8} sm={9} style={{ paddingLeft: "2rem" }}>
                      <div className="text-primary" style={{ padding: '2.5rem 3rem 0rem 8vw' }}>
                        <p style={{ fontSize: '0.85rem', color:'#1075c5', marginTop: '0.8rem' }}
                        >{t('Sign in to TheChart.AI platform')}</p>
                      </div>
                      <LanguageSwitcher />
                    </Col>
                  </Row>
                </div>
                */}
                <CardBody 
                  className="pt-0"
                  style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}
                >

                  <div 
                    style={{ 
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      marginTop: "100px"
                    }}
                  >
                    <div style={{ width: "30%" }}>
                      <img
                        src={logo}
                        alt="ttmy_logo"
                      />
                    </div>
                  </div>

                  {/*
                  <div>
                    <Link to="/" className="logo-light-element">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img
                            src={logo}
                            alt=""
                            className="rounded-circle"
                            height="34"
                          />
                        </span>
                      </div>
                    </Link>
                  </div>
                  */}

                  <div style={{ padding: '5vh 8vw 5vh 8vw', flex: '1 1 auto' }}>
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit(e);
                        {/*navigate('/dashboard')*/}
                        return false;
                      }}
                    >

                      {/*--- Login Error ---*/}
                      {!googleErrorMessage && errorMessage &&
                        <Alert color="danger">
                          {errorMessage}
                          {errorMessage === "帳號尚未驗證，請先至註冊信箱收取驗證信，並點擊連結驗證您的帳號" && (
                            <span>
                              {" "}
                              <a href="#" onClick={() => resendVerificationEmail(validation.values.email)}>重寄驗證信</a>
                            </span>
                          )}
                        </Alert>
                      }

                      {/*--- google Login Error ---*/}
                      {!errorMessage && 
                        googleErrorMessage 
                        ? <Alert color="danger">{googleErrorMessage}</Alert> 
                        : null
                      }

                      {/*
                      {true && (<>
                        <Alert color="success">{errorMessage}</Alert> 
                        <Alert color="warning">{googleErrorMessage}</Alert> 
                        </>
                        )
                      }
                      */}


                      <div className="mb-3">
                        <Input
                          name="email"
                          className="form-control"
                          placeholder="電話號碼、用戶名、或 email"
                          type="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={!!(validation.touched.email && validation.errors.email)}
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                          <Input
                            name="password"
                            value={validation.values.password || ""}
                            type={viewPassword ? 'text' : 'password'}
                            placeholder={t('Enter your password')}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={!!(validation.touched.password && validation.errors.password)}
                            style={{ letterSpacing: (viewPassword||!validation.values.password) ? '0px' : '2.5px' }}
                            onKeyPress={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();  // Prevent the default form submit behavior
                                // Assuming there's a function to handle form submission:
                                //handleSubmit(e);     // Replace `handleSubmit` with your actual submission handler
                                validation.handleSubmit();  // This triggers Formik's validation and submission
                              }
                            }}
                          />
                          {validation.touched.password && validation.errors.password ? (
                            <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                          ) : null}

                          <button 
                            onClick={e => {
                              e.preventDefault();
                              togglePasswordVisibility(e);
                            }}
                            aria-label="Toggle password visibility"
                            style={{
                              position: 'absolute',
                              top: viewPassword ? '9px' : '9px',
                              right: (validation.touched.password && validation.errors.password) ? '30px' : '10px',
                              border: 'none',
                              background: 'none',
                              color: '#556ee6',
                              cursor: 'pointer'
                            }}
                            type="button"
                          >
                            <FontAwesomeIcon icon={viewPassword ? faEyeSlash : faEye} />
                          </button>
                        </div>

                      </div>


                      <div className="d-flex justify-content-between" style={{ width: "100%" }}>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="customControlInline"
                            onClick={handleSaveAccountChange}
                            onChange={()=>{}}
                            checked={saveAccount}
                            //onChange={(e) => setSaveAccount(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="customControlInline"
                            style={{ marginLeft: '1px', marginTop: '0.5px' }}
                          >
                           {t('Remember Email')}
                          </label>
                        </div>
                        <div>
                          <p className="mb-0">
                            <Link to="/forgot-password" className="text-muted">
                              <i className="mdi mdi-lock me-1" />
                              {t('Forgot Password?')}
                            </Link>
                          </p>
                        </div>
                      </div>

                      <div style={{ display:'flex', justifyContent: 'center', marginTop: '50px' }}>
                        <button
                          className="btn btn-outline-warning btn-block"
                          type="submit"
                          style={{ 
                            width:'100%',
                            color: '#1d6cac',
                            borderColor: '#318cd7'
                          }}
                        >
                        {loading &&
                          (<i className="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>)
                        }
                          {t('Sign in')}
                        </button>
                      </div>


                      <OrDivider />


                      {/* Other Login Methods */}
                      <div className="mt-4 text-center">

                        {/*<h4 className="font-size-13 mb-3">{t('Or sign in with Google Account')}</h4>*/}
  
                        {/* Google Login */}
                        <ul className="list-inline" style={{ height: "3.0rem" }}>
                          
                          <li className="list-inline-item">
                            <div id="buttonDiv" ref={divRef} />
                          </li>

                        </ul>
                        {/* Google Login Tail */}

                      </div>

                    </Form>
                  </div>

                  <div style={{ padding: '0 20px 30px 20px', marginTop: 'auto' }}>
                    <div style={{ width: '100vw', marginLeft: "-35px" }}>
                      <div style={{ width: '100%', flex: '1', height: "1px", backgroundColor: "#dbdbdb" }}></div>
                    </div>
                    <div className="text-center" style={{ marginTop: '30px', marginBottom: '15px' }}>
                      <p style={{ marginBottom: '0' }}>
                        {t('Don\'t have an account yet?')}{" "}
                        <Link to="/register" className="fw-medium" style={{ color: '#328cd7' }}>
                          {t('Register an account')}{" "}
                        </Link>
                      </p>
                    {/*
                    <p>
                      © {new Date().getFullYear()} TheChart.AI Crafted with{" "}
                      <i className="mdi mdi-chart-bubble" style={{ color:'#9d6237' }}/>(bubbles) in Taipei
                      <i className="mdi mdi-cube-outline" style={{ color: '#1E90FF' }} /> (ice cubes) in Taipei
                    </p>
                    */}
                    </div>
                  </div>
                  

                </CardBody>
              </Card>


            </Col>

            <Col md={6}
              style={{
                padding: '0',
                height: '100vh'
              }}
              className="d-none d-md-none"
            >
              <img
                src={Christmas_Landing}
                alt="Christmas_Landing"
                style={{
                  maxHeight: '100vh',
                  minHeight: '100vh',
                  minWidth: '50vw',
                  objectFit: 'cover'
                }}
              />
            </Col>

          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(Login);

Login.propTypes = {
  history: PropTypes.object,
};

/*---- Optional Redirect Pattern for Google Login ----*/

  /*
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      // Create an object with the token
      const tokenObject = { token: token };
      // Store the object in localStorage
      localStorage.setItem('authUser', JSON.stringify(tokenObject));
      navigate('/dashboard');
    }
    // If there's no token, stay on the login page
  }, [dispatch]);
  */

/*---- Optional Redirect Pattern for Google Login Tail ----*/



